import type {
  AdminPortalUserResponse,
  AdminPortalUsersResponse,
  DeleteEventResponse,
  DeletePortalUserResponse,
  DisplayNumberResponse,
  EntraDirectorySearchResponse,
  EventMembershipResponse,
  EventPhoneReservationsResponse,
  EventParticipantInput,
  EventParticipantsResponse,
  EventResponse,
  GetDisplayNumbersResponse,
  GetEventsResponse,
  GetMeResponse,
  GetTelephonesResponse,
  GuestInvitationAcceptanceInput,
  GuestInvitationAcceptanceResponse,
  GuestInvitationInput,
  GuestInvitationsResponse,
  GuestInvitationResponse,
  GuestInvitationSmsInput,
  GuestInvitationSmsResponse,
  ImportEntraPortalUserResponse,
  PortalEventCandidate,
  PortalEventInput,
  PortalDisplayNumberInput,
  PortalEventParticipant,
  PortalBookingResponse,
  PortalBookingReleaseResponse,
  PortalBookingStateResponse,
  PortalGuestLoginInput,
  PortalGuestLoginResponse,
  PortalSignInResponse,
  PortalSignOutResponse,
  PortalTelephoneCreateInput,
  PortalTelephoneUpdateInput,
  RemoveEventParticipantResponse,
  TelephonePasswordResponse,
  TelephoneResponse,
} from '../types/api';

export class PortalApiError extends Error {
  public readonly status?: number;

  public constructor(message: string, status?: number) {
    super(message);
    this.name = 'PortalApiError';
    this.status = status;
  }
}

async function portalRequest<T>(
  path: string,
  accessToken: string | null,
  init: RequestInit = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        'X-Portal-Request': '1',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new PortalApiError('Could not connect to the portal API.');
  }

  if (!response.ok) {
    let message = `The API returned HTTP ${response.status}.`;
    try {
      const body = (await response.json()) as { error?: string };
      message = body.error ?? message;
    } catch {
      // Keep the HTTP fallback message.
    }
    throw new PortalApiError(message, response.status);
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new PortalApiError('The API returned a response that could not be read.', response.status);
  }
}

export function startPortalSignIn(email: string): Promise<PortalSignInResponse> {
  return publicPortalRequest<PortalSignInResponse>('/api/auth/start', { email });
}

export function confirmPortalGuestLogin(
  input: PortalGuestLoginInput,
): Promise<PortalGuestLoginResponse> {
  return publicPortalRequest<PortalGuestLoginResponse>('/api/auth/guest/verify', input);
}

export function signOutPortalGuest(): Promise<PortalSignOutResponse> {
  return publicPortalRequest<PortalSignOutResponse>('/api/auth/logout', {});
}

export function getPortalStatus(
  accessToken: string,
  signal?: AbortSignal,
): Promise<GetMeResponse> {
  return portalRequest<GetMeResponse>('/api/me', accessToken, { method: 'GET', signal });
}

export function getPortalEvents(accessToken: string): Promise<GetEventsResponse> {
  return portalRequest<GetEventsResponse>('/api/events', accessToken, { method: 'GET' });
}

export function createPortalEvent(
  accessToken: string,
  input: PortalEventInput,
): Promise<EventResponse> {
  return portalRequest<EventResponse>('/api/events', accessToken, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updatePortalEvent(
  accessToken: string,
  eventId: string,
  input: PortalEventInput,
): Promise<EventResponse> {
  return portalRequest<EventResponse>(
    `/api/events/${encodeURIComponent(eventId)}`,
    accessToken,
    { method: 'PUT', body: JSON.stringify(input) },
  );
}

export function deletePortalEvent(
  accessToken: string,
  eventId: string,
): Promise<DeleteEventResponse> {
  return portalRequest<DeleteEventResponse>(
    `/api/events/${encodeURIComponent(eventId)}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export function getPortalDisplayNumbers(
  accessToken: string,
): Promise<GetDisplayNumbersResponse> {
  return portalRequest<GetDisplayNumbersResponse>('/api/display-numbers', accessToken, {
    method: 'GET',
    cache: 'no-store',
  });
}

export function createPortalDisplayNumber(
  accessToken: string,
  input: PortalDisplayNumberInput,
): Promise<DisplayNumberResponse> {
  return portalRequest<DisplayNumberResponse>('/api/display-numbers', accessToken, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updatePortalDisplayNumber(
  accessToken: string,
  displayNumberId: string,
  input: PortalDisplayNumberInput,
): Promise<DisplayNumberResponse> {
  return portalRequest<DisplayNumberResponse>(
    `/api/display-numbers/${encodeURIComponent(displayNumberId)}`,
    accessToken,
    { method: 'PUT', body: JSON.stringify(input) },
  );
}

export function getPortalTelephones(accessToken: string): Promise<GetTelephonesResponse> {
  return portalRequest<GetTelephonesResponse>('/api/telephones', accessToken, { method: 'GET' });
}

export function createPortalTelephone(
  accessToken: string,
  input: PortalTelephoneCreateInput,
): Promise<TelephoneResponse> {
  return portalRequest<TelephoneResponse>('/api/telephones', accessToken, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updatePortalTelephone(
  accessToken: string,
  phoneNumber: string,
  input: PortalTelephoneUpdateInput,
): Promise<TelephoneResponse> {
  return portalRequest<TelephoneResponse>(
    `/api/telephones/${encodeURIComponent(phoneNumber)}`,
    accessToken,
    { method: 'PUT', body: JSON.stringify(input) },
  );
}

export function getPortalTelephonePassword(
  accessToken: string,
  phoneNumber: string,
): Promise<TelephonePasswordResponse> {
  return portalRequest<TelephonePasswordResponse>(
    `/api/telephones/${encodeURIComponent(phoneNumber)}`,
    accessToken,
    { method: 'GET', cache: 'no-store' },
  );
}


type EventParticipantsWireResponse = Omit<
  EventParticipantsResponse,
  'Participants' | 'Candidates'
> & {
  Participants: PortalEventParticipant[] | PortalEventParticipant | null;
  Candidates: PortalEventCandidate[] | PortalEventCandidate | null;
};

function normalizeArray<T>(value: T[] | T | null | undefined): T[] {
  if (value == null) return [];
  return Array.isArray(value)
    ? value.filter((item): item is T => item != null)
    : [value];
}

export async function getPortalEventParticipants(
  accessToken: string,
  eventId: string,
): Promise<EventParticipantsResponse> {
  const response = await portalRequest<EventParticipantsWireResponse>(
    `/api/events/${encodeURIComponent(eventId)}/participants`,
    accessToken,
    { method: 'GET', cache: 'no-store' },
  );

  return {
    ...response,
    Participants: normalizeArray(response.Participants),
    Candidates: normalizeArray(response.Candidates),
  };
}

export function setPortalEventParticipant(
  accessToken: string,
  eventId: string,
  input: EventParticipantInput,
): Promise<EventMembershipResponse> {
  return portalRequest<EventMembershipResponse>(
    `/api/events/${encodeURIComponent(eventId)}/participants`,
    accessToken,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function removePortalEventParticipant(
  accessToken: string,
  eventId: string,
  loginId: string,
): Promise<RemoveEventParticipantResponse> {
  return portalRequest<RemoveEventParticipantResponse>(
    `/api/events/${encodeURIComponent(eventId)}/participants/${encodeURIComponent(loginId)}`,
    accessToken,
    { method: 'DELETE' },
  );
}


export function getAdminPortalUsers(
  accessToken: string,
): Promise<AdminPortalUsersResponse> {
  return portalRequest<AdminPortalUsersResponse>(
    '/api/portal-users',
    accessToken,
    { method: 'GET', cache: 'no-store' },
  );
}

export function searchAdminEntraUsers(
  accessToken: string,
  query: string,
): Promise<EntraDirectorySearchResponse> {
  return portalRequest<EntraDirectorySearchResponse>(
    `/api/entra-users?query=${encodeURIComponent(query)}`,
    accessToken,
    { method: 'GET', cache: 'no-store' },
  );
}

export function importAdminEntraUser(
  accessToken: string,
  entraObjectId: string,
): Promise<ImportEntraPortalUserResponse> {
  return portalRequest<ImportEntraPortalUserResponse>(
    '/api/entra-users',
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({ entraObjectId }),
    },
  );
}

export function updateAdminPortalUserActiveState(
  accessToken: string,
  loginId: string,
  isActive: boolean,
): Promise<AdminPortalUserResponse> {
  return portalRequest<AdminPortalUserResponse>(
    `/api/portal-users/${encodeURIComponent(loginId)}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify({ isActive }) },
  );
}

export function updateAdminPortalUserMobileNumber(
  accessToken: string,
  loginId: string,
  mobileNumber: string,
): Promise<AdminPortalUserResponse> {
  return portalRequest<AdminPortalUserResponse>(
    `/api/portal-users/${encodeURIComponent(loginId)}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify({ mobileNumber }) },
  );
}

export function updateAdminPortalUserEventManager(
  accessToken: string,
  loginId: string,
  isEventManager: boolean,
): Promise<AdminPortalUserResponse> {
  return portalRequest<AdminPortalUserResponse>(
    `/api/portal-users/${encodeURIComponent(loginId)}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify({ isEventManager }) },
  );
}


export function deleteAdminPortalUser(
  accessToken: string,
  loginId: string,
): Promise<DeletePortalUserResponse> {
  return portalRequest<DeletePortalUserResponse>(
    `/api/portal-users/${encodeURIComponent(loginId)}`,
    accessToken,
    { method: 'DELETE' },
  );
}


export function invitePortalEventGuest(
  accessToken: string,
  eventId: string,
  input: GuestInvitationInput,
): Promise<GuestInvitationResponse> {
  return portalRequest<GuestInvitationResponse>(
    `/api/events/${encodeURIComponent(eventId)}/guest-invitations`,
    accessToken,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function getPortalEventGuestInvitations(
  accessToken: string,
  eventId: string,
): Promise<GuestInvitationsResponse> {
  return portalRequest<GuestInvitationsResponse>(
    `/api/events/${encodeURIComponent(eventId)}/guest-invitations`,
    accessToken,
    { method: 'GET', cache: 'no-store' },
  );
}

export function updatePortalEventGuestInvitation(
  accessToken: string,
  eventId: string,
  invitationId: string,
  input: GuestInvitationInput,
): Promise<GuestInvitationResponse> {
  return portalRequest<GuestInvitationResponse>(
    `/api/events/${encodeURIComponent(eventId)}/guest-invitations/${encodeURIComponent(invitationId)}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

export async function acceptPortalGuestInvitation(
  input: GuestInvitationAcceptanceInput,
): Promise<GuestInvitationAcceptanceResponse> {
  let response: Response;
  try {
    response = await fetch('/api/guest-invitations/accept', {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Portal-Request': '1' },
      body: JSON.stringify(input),
    });
  } catch {
    throw new PortalApiError('Could not connect to the portal API.');
  }

  if (!response.ok) {
    let message = `The API returned HTTP ${response.status}.`;
    try {
      const body = (await response.json()) as { error?: string };
      message = body.error ?? message;
    } catch {
      // Keep the HTTP fallback message.
    }
    throw new PortalApiError(message, response.status);
  }

  return (await response.json()) as GuestInvitationAcceptanceResponse;
}

export async function resendPortalGuestInvitationSms(
  input: GuestInvitationSmsInput,
): Promise<GuestInvitationSmsResponse> {
  return publicPortalRequest<GuestInvitationSmsResponse>(
    '/api/guest-invitations/resend-sms',
    input,
  );
}

async function publicPortalRequest<T>(path: string, body: object): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Portal-Request': '1' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new PortalApiError('Could not connect to the portal API.');
  }
  if (!response.ok) {
    let message = `The API returned HTTP ${response.status}.`;
    try {
      const errorBody = (await response.json()) as { error?: string };
      message = errorBody.error ?? message;
    } catch {
      // Keep the HTTP fallback message.
    }
    throw new PortalApiError(message, response.status);
  }
  return (await response.json()) as T;
}


type EventPhoneReservationsWireResponse = Omit<
  EventPhoneReservationsResponse,
  'Reservations' | 'Telephones' | 'ActiveBookings'
> & {
  Reservations: EventPhoneReservationsResponse['Reservations'] | EventPhoneReservationsResponse['Reservations'][number] | null;
  Telephones: EventPhoneReservationsResponse['Telephones'] | EventPhoneReservationsResponse['Telephones'][number] | null;
  ActiveBookings: EventPhoneReservationsResponse['ActiveBookings'] | EventPhoneReservationsResponse['ActiveBookings'][number] | null;
};

export async function getPortalEventPhoneReservations(
  accessToken: string,
  eventId: string,
): Promise<EventPhoneReservationsResponse> {
  const response = await portalRequest<EventPhoneReservationsWireResponse>(
    `/api/events/${encodeURIComponent(eventId)}/phone-reservations`,
    accessToken,
    { method: 'GET', cache: 'no-store' },
  );
  return {
    ...response,
    Reservations: normalizeArray(response.Reservations),
    Telephones: normalizeArray(response.Telephones),
    ActiveBookings: normalizeArray(response.ActiveBookings),
  };
}

export async function updatePortalEventPhoneReservations(
  accessToken: string,
  eventId: string,
  phoneNumbers: string[],
): Promise<EventPhoneReservationsResponse> {
  const response = await portalRequest<EventPhoneReservationsWireResponse>(
    `/api/events/${encodeURIComponent(eventId)}/phone-reservations`,
    accessToken,
    { method: 'PUT', body: JSON.stringify({ phoneNumbers }) },
  );
  return {
    ...response,
    Reservations: normalizeArray(response.Reservations),
    Telephones: normalizeArray(response.Telephones),
    ActiveBookings: normalizeArray(response.ActiveBookings),
  };
}

type BookingStateWireResponse = Omit<
  PortalBookingStateResponse,
  'EligibleEvents' | 'CallingPurposes'
> & {
  EligibleEvents: PortalBookingStateResponse['EligibleEvents'] | PortalBookingStateResponse['EligibleEvents'][number] | null;
  CallingPurposes?: PortalBookingStateResponse['CallingPurposes'] | PortalBookingStateResponse['CallingPurposes'][number] | null;
};

export async function getPortalBookingState(
  accessToken: string,
): Promise<PortalBookingStateResponse> {
  const response = await portalRequest<BookingStateWireResponse>(
    '/api/bookings',
    accessToken,
    { method: 'GET', cache: 'no-store' },
  );
  return {
    ...response,
    EligibleEvents: normalizeArray(response.EligibleEvents),
    CallingPurposes: normalizeArray(response.CallingPurposes),
  };
}

export function createPortalPhoneBooking(
  accessToken: string,
  eventId: string,
  callerDisplayNumberId: string,
): Promise<PortalBookingResponse> {
  return portalRequest<PortalBookingResponse>('/api/bookings', accessToken, {
    method: 'POST',
    body: JSON.stringify({ eventId, callerDisplayNumberId }),
  });
}

export function releasePortalPhoneBooking(
  accessToken: string,
  bookingId: string,
): Promise<PortalBookingReleaseResponse> {
  return portalRequest<PortalBookingReleaseResponse>(
    `/api/bookings/${encodeURIComponent(bookingId)}`,
    accessToken,
    { method: 'DELETE' },
  );
}
